import { DragDropContext, Draggable, DragStart, DragUpdate, Droppable, DropResult, ResponderProvided } from '@hello-pangea/dnd';
import { ExclamationTriangleIcon, MapPinIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { CSSProperties, KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { InlineBanner } from '../../components/ui/InlineBanner';
import { Segmented } from '../../components/ui/Segmented';
import { Skeleton, SkeletonRegion } from '../../components/ui/Skeleton';
import { StateBlock } from '../../components/ui/StateBlock';
import { Switch } from '../../components/ui/Switch';
import { notify } from '../../components/ui/toast';
import { STAFF_ABOVE } from '../../constants/roles';
import useAuth from '../../hooks/useAuth';
import { useActiveLocation } from '../../hooks/useActiveLocation';
import { useApiError } from '../../hooks/useApiError';
import { playChime } from '../../lib/sound';
import type { Order, OrderStatus } from '../../types/Order';
import { BOARD_STATUSES, BoardStatus, COLUMNS_QUERY, OrdersLayout, REPEAT_AFTER_MS, REPEAT_CHIME_MS, TypeFilter, UNSEEN_VISIBLE_MS } from './config';
import { itemCount } from './orderApi';
import { OrderTable } from './OrderTable';
import { OrderTicket } from './OrderTicket';
import { useAcceptingOrders } from './useAcceptingOrders';
import { useMediaQuery } from './useMediaQuery';
import { useOrderActions } from './useOrderActions';
import { FeedState, useOrdersFeed } from './useOrdersFeed';

const CLOCK_MS = 30_000;

const useNow = (ms: number): number => {
    const [now, setNow] = useState(Date.now);
    useEffect(() => {
        const id = window.setInterval(() => setNow(Date.now()), ms);
        return () => window.clearInterval(id);
    }, [ms]);
    return now;
};

const DOT: Record<FeedState, string> = { live: 'bg-chart-3', reconnecting: 'bg-chart-2', offline: 'bg-danger-solid' };

/** Live indicator. Only the state word is a live region; the ticking seconds are not announced. */
const LiveStatus = ({ state, lastUpdated }: { state: FeedState; lastUpdated: number }) => {
    const { t, i18n } = useTranslation('orders');
    const now = useNow(1000);
    const seconds = Math.max(0, Math.round((now - lastUpdated) / 1000));
    const ago = seconds < 60 ? t('live.updatedSeconds', { n: seconds }) : t('live.updatedMinutes', { n: Math.floor(seconds / 60) });
    return (
        <p className="flex min-h-control items-center gap-2 text-caption text-strong">
            <span aria-hidden="true" className={clsx('size-2 rounded-chip', DOT[state])} />
            <span role="status" className={state === 'offline' ? 'font-semibold text-danger' : undefined}>
                {state === 'offline'
                    ? t('live.offline', {
                          time: lastUpdated ? new Date(lastUpdated).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' }) : '–',
                      })
                    : t(`live.${state}`)}
            </span>
            {state === 'live' && lastUpdated > 0 && <span className="tabular-nums text-muted">· {ago}</span>}
        </p>
    );
};

const BoardSkeleton = () => (
    <SkeletonRegion>
        <div className="grid gap-4 lg:grid-cols-4">
            {Array.from({ length: 4 }, (_, i) => (
                <Card key={i} className="flex flex-col gap-3">
                    <Skeleton className="h-12 w-full" />
                    <Skeleton className="w-2/3" />
                    <Skeleton className="h-14 w-full" />
                </Card>
            ))}
        </div>
    </SkeletonRegion>
);

const matchesType = (o: Order, filter: TypeFilter) => filter === 'all' || o.orderType === filter;

const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** A drop animation needs a transition to end, so reduced motion shortens it instead of removing it. */
const calm = (style: CSSProperties | undefined): CSSProperties | undefined =>
    style && style.transition && reduceMotion() ? { ...style, transitionDuration: '0.001s' } : style;

interface Props {
    locationId: string | null;
    layout: OrdersLayout;
}

/** The shift board for one location (or all, for a non-Admin with several). */
const LiveBoard = ({ locationId, layout }: Props) => {
    const { t } = useTranslation(['orders', 'common']);
    const { hasRole } = useAuth();
    const apiError = useApiError();
    const feed = useOrdersFeed(locationId);
    const actions = useOrderActions(feed);
    const { accepting, set: setAccepting } = useAcceptingOrders(locationId);
    const canPause = hasRole(STAFF_ABOVE);
    const now = useNow(CLOCK_MS);
    const columnsShown = useMediaQuery(COLUMNS_QUERY);
    const [tab, setTab] = useState<BoardStatus>('pending');
    const [filter, setFilter] = useState<TypeFilter>('all');
    const [cancelTarget, setCancelTarget] = useState<Order | null>(null);
    const [cancelling, setCancelling] = useState(false);
    const [seen, setSeen] = useState<ReadonlySet<string>>(new Set());
    const [announcement, setAnnouncement] = useState('');
    const [settled, setSettled] = useState(false);
    const announced = useRef(new Set<string>());
    const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

    const markSeen = useCallback((id: string) => setSeen((s) => (s.has(id) ? s : new Set(s).add(id))), []);
    const unseen = useMemo(() => new Set([...feed.newIds].filter((id) => !seen.has(id))), [feed.newIds, seen]);

    // Tickets that mount after the first paint of real data animate in.
    useEffect(() => {
        if (feed.loaded) setSettled(true);
    }, [feed.loaded]);

    // The table has no "half visible" tickets: the list counts as seen once it has been on screen with the tab focused.
    useEffect(() => {
        if (layout !== 'table' || unseen.size === 0) return;
        let timer: number | undefined;
        const arm = () => {
            timer = window.setTimeout(() => {
                if (document.hasFocus() && !document.hidden) setSeen((s) => new Set([...s, ...unseen]));
                else arm();
            }, UNSEEN_VISIBLE_MS);
        };
        arm();
        return () => window.clearTimeout(timer);
    }, [layout, unseen]);

    // Screen reader announcement for each order that arrives.
    useEffect(() => {
        const fresh = feed.orders.filter((o) => feed.newIds.has(o.id) && !announced.current.has(o.id));
        if (fresh.length === 0) return;
        fresh.forEach((o) => announced.current.add(o.id));
        setAnnouncement(
            fresh
                .map((o) =>
                    t(o.orderType === 'togo' ? 'live.newOrderTogo' : 'live.newOrderTable', {
                        number: o.orderNumber,
                        table: o.tableNumber ?? '',
                        count: itemCount(o),
                    })
                )
                .join(' ')
        );
    }, [feed.orders, feed.newIds, t]);

    // The arrival chime comes from the shell's pending-count poll; this repeats it for orders nobody has looked at.
    const watch = useRef({ orders: feed.orders, unseen });
    watch.current = { orders: feed.orders, unseen };
    useEffect(() => {
        const id = window.setInterval(() => {
            const { orders, unseen: u } = watch.current;
            const stale = orders.some((o) => o.status === 'pending' && u.has(o.id) && Date.now() - Date.parse(o.createdAt) > REPEAT_AFTER_MS);
            if (stale) void playChime();
        }, REPEAT_CHIME_MS);
        return () => window.clearInterval(id);
    }, []);

    const visible = useMemo(() => feed.orders.filter((o) => matchesType(o, filter)), [feed.orders, filter]);
    const byAge = (a: Order, b: Order) => Date.parse(a.createdAt) - Date.parse(b.createdAt);

    const columns = useMemo(() => {
        const by: Record<BoardStatus, Order[]> = { pending: [], accepted: [], preparing: [], ready: [] };
        for (const o of visible) {
            if ((BOARD_STATUSES as readonly OrderStatus[]).includes(o.status)) by[o.status as BoardStatus].push(o);
        }
        (Object.values(by) as Order[][]).forEach((list) => list.sort(byAge));
        return by;
    }, [visible]);
    const rows = useMemo(() => [...visible].sort(byAge), [visible]);

    const wrap = <A extends unknown[]>(fn: (order: Order, ...a: A) => Promise<void>) => (order: Order, ...a: A) => {
        markSeen(order.id);
        void fn(order, ...a);
    };
    const onAdvance = wrap(actions.advance);
    const onPay = wrap(actions.pay);
    const onMove = wrap(actions.moveTo);

    const confirmCancel = async () => {
        if (!cancelTarget) return;
        setCancelling(true);
        markSeen(cancelTarget.id);
        await actions.cancel(cancelTarget);
        setCancelling(false);
        setCancelTarget(null);
    };

    const toggleAccepting = async (value: boolean) => {
        try {
            await setAccepting(value);
        } catch (e) {
            notify.error(apiError(e));
        }
    };

    const onTabKey = (e: KeyboardEvent, index: number) => {
        const last = BOARD_STATUSES.length - 1;
        const target =
            e.key === 'ArrowRight' ? (index + 1) % BOARD_STATUSES.length : e.key === 'ArrowLeft' ? (index - 1 + BOARD_STATUSES.length) % BOARD_STATUSES.length : e.key === 'Home' ? 0 : e.key === 'End' ? last : null;
        if (target === null) return;
        e.preventDefault();
        setTab(BOARD_STATUSES[target]);
        tabRefs.current[target]?.focus();
    };

    // Drag and drop: the same move as the "Move to" menu, so the undo toast, the rules and the rollback are shared.
    const orderById = (id: string) => feed.orders.find((o) => o.id === id);
    const statusName = (id: string) => t(`common:status.${id}`);
    const onDragStart = (start: DragStart, provided: ResponderProvided) => {
        const order = orderById(start.draggableId);
        if (order) provided.announce(t('dnd.lifted', { number: order.orderNumber, status: statusName(start.source.droppableId) }));
    };
    const onDragUpdate = (update: DragUpdate, provided: ResponderProvided) => {
        const order = orderById(update.draggableId);
        if (!order) return;
        const to = update.destination?.droppableId;
        provided.announce(
            !to
                ? t('dnd.outside', { number: order.orderNumber })
                : to === update.source.droppableId
                  ? t('dnd.backIn', { number: order.orderNumber, status: statusName(to) })
                  : t('dnd.over', { number: order.orderNumber, status: statusName(to) })
        );
    };
    const onDragEnd = (result: DropResult, provided: ResponderProvided) => {
        const order = orderById(result.draggableId);
        if (!order) return;
        const to = result.destination?.droppableId as BoardStatus | undefined;
        if (result.reason === 'CANCEL' || !to || to === order.status) {
            provided.announce(t('dnd.stayed', { number: order.orderNumber, status: statusName(order.status) }));
            return;
        }
        provided.announce(t('dnd.dropped', { number: order.orderNumber, status: statusName(to) }));
        markSeen(order.id);
        const moved = actions.moveTo(order, to);
        // The ticket cannot be picked up while it saves, which drops keyboard focus: give it back once it can.
        if (result.mode === 'SNAP') {
            void moved.then(() =>
                window.setTimeout(() => document.querySelector<HTMLElement>(`[data-rfd-drag-handle-draggable-id="${order.id}"]`)?.focus(), 50)
            );
        }
    };

    const failedFirstLoad = !feed.loaded && feed.state === 'offline';
    const dragOn = columnsShown && layout === 'board';

    return (
        <div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <LiveStatus state={feed.state} lastUpdated={feed.lastUpdated} />
                <Segmented
                    aria-label={t('filter.label')}
                    value={filter}
                    onChange={setFilter}
                    options={[
                        { value: 'all', label: t('filter.all') },
                        { value: 'table', label: t('filter.table') },
                        { value: 'togo', label: t('common:orderType.togo') },
                    ]}
                />
                <div className="ml-auto flex flex-wrap items-center gap-3">
                    {canPause && accepting !== null && (
                        <div className="rounded-control bg-surface px-3 shadow-hairline">
                            <Switch checked={accepting} onChange={(v) => void toggleAccepting(v)} label={t('accepting.label')} />
                        </div>
                    )}
                </div>
            </div>

            <div className="mt-4 flex flex-col gap-3 empty:hidden">
                {feed.state === 'offline' && feed.loaded && (
                    <InlineBanner tone="neutral-strong" icon={ExclamationTriangleIcon} action={{ label: t('live.retry'), onClick: feed.refresh }}>
                        {t('live.offlineBanner')}
                    </InlineBanner>
                )}
                {accepting === false && (
                    <InlineBanner
                        tone="neutral-strong"
                        action={canPause ? { label: t('accepting.resume'), onClick: () => void toggleAccepting(true) } : undefined}
                    >
                        {t('accepting.paused')}
                    </InlineBanner>
                )}
            </div>

            <div aria-live="assertive" className="sr-only">
                {announcement}
            </div>

            {failedFirstLoad ? (
                <Card padding="none" className="mt-4">
                    <StateBlock
                        variant="error"
                        title={t('board.loadFailed')}
                        body={t('common:ui.checkConnection')}
                        action={<Button onClick={feed.refresh}>{t('common:ui.retry')}</Button>}
                    />
                </Card>
            ) : !feed.loaded ? (
                <div className="mt-4">
                    <BoardSkeleton />
                </div>
            ) : layout === 'table' ? (
                <div className="mt-4">
                    <OrderTable
                        orders={rows}
                        now={now}
                        saving={feed.saving}
                        filtered={filter !== 'all' && feed.orders.length > 0}
                        showLocation={locationId === null}
                        onAdvance={onAdvance}
                        onPay={onPay}
                        onMove={onMove}
                        onCancel={setCancelTarget}
                    />
                </div>
            ) : (
                <>
                    <div
                        role="tablist"
                        aria-label={t('board.tabs')}
                        className="sticky top-0 z-10 -mx-gutter mt-2 flex gap-2 overflow-x-auto bg-page px-gutter py-2 lg:hidden"
                    >
                        {BOARD_STATUSES.map((status, i) => {
                            const selected = tab === status;
                            return (
                                <button
                                    key={status}
                                    ref={(el) => {
                                        tabRefs.current[i] = el;
                                    }}
                                    id={`tab-${status}`}
                                    type="button"
                                    role="tab"
                                    aria-selected={selected}
                                    aria-controls={`panel-${status}`}
                                    tabIndex={selected ? 0 : -1}
                                    onClick={() => setTab(status)}
                                    onKeyDown={(e) => onTabKey(e, i)}
                                    className={clsx(
                                        'inline-flex h-11 shrink-0 items-center gap-2 rounded-chip px-4 text-small font-medium',
                                        selected ? 'bg-inverse text-on-inverse' : 'bg-surface text-strong shadow-hairline'
                                    )}
                                >
                                    {t(`common:status.${status}`)}
                                    <Badge count={columns[status].length} tone={status === 'pending' && columns[status].length > 0 ? 'action' : 'neutral'} />
                                </button>
                            );
                        })}
                    </div>

                    <DragDropContext
                        onDragStart={onDragStart}
                        onDragUpdate={onDragUpdate}
                        onDragEnd={onDragEnd}
                        dragHandleUsageInstructions={t('dnd.instructions')}
                    >
                        <div className="mt-2 grid gap-4 lg:grid-cols-4 lg:gap-3 xl:gap-4">
                            {BOARD_STATUSES.map((status) => {
                                const list = columns[status];
                                return (
                                    <section
                                        key={status}
                                        id={`panel-${status}`}
                                        role="tabpanel"
                                        aria-labelledby={`tab-${status}`}
                                        className={clsx('min-w-0 lg:block lg:min-w-[14.5rem]', tab === status ? 'block' : 'hidden')}
                                    >
                                        <h2 className="sticky top-0 z-[5] mb-3 flex h-11 items-center gap-2 bg-page text-title text-strong max-lg:sr-only">
                                            {t(`common:status.${status}`)}
                                            <Badge count={list.length} tone={status === 'pending' && list.length > 0 ? 'action' : 'neutral'} />
                                        </h2>
                                        <Droppable droppableId={status} isDropDisabled={!dragOn}>
                                            {(drop, dropState) => (
                                                <div
                                                    ref={drop.innerRef}
                                                    {...drop.droppableProps}
                                                    role="list"
                                                    aria-label={t(`common:status.${status}`)}
                                                    className={clsx(
                                                        'grid min-h-24 gap-3 rounded-card sm:grid-cols-2 lg:min-h-[calc(100dvh-15rem)] lg:grid-cols-1 lg:content-start lg:max-h-[calc(100dvh-15rem)] lg:overflow-y-auto lg:pb-2 lg:pr-1',
                                                        dropState.isDraggingOver && 'bg-sunken ring-2 ring-focus'
                                                    )}
                                                >
                                                    {list.length === 0 && (
                                                        <p className="flex h-24 items-center justify-center rounded-card border border-dashed border-control px-3 text-center text-small text-muted sm:col-span-2 lg:col-span-1">
                                                            {t(`board.empty.${status}`)}
                                                        </p>
                                                    )}
                                                    {list.map((order, index) => (
                                                        <Draggable key={order.id} draggableId={order.id} index={index} isDragDisabled={!dragOn || feed.saving.has(order.id)}>
                                                            {(drag, dragState) => (
                                                                <div
                                                                    ref={drag.innerRef}
                                                                    {...drag.draggableProps}
                                                                    style={calm(drag.draggableProps.style)}
                                                                    role="listitem"
                                                                    className="min-w-0"
                                                                >
                                                                    <OrderTicket
                                                                        order={order}
                                                                        now={now}
                                                                        unseen={unseen.has(order.id)}
                                                                        animateIn={settled}
                                                                        showLocation={locationId === null}
                                                                        saving={feed.saving.has(order.id)}
                                                                        dragHandleProps={drag.dragHandleProps}
                                                                        dragging={dragState.isDragging}
                                                                        onSeen={markSeen}
                                                                        onAdvance={onAdvance}
                                                                        onPay={onPay}
                                                                        onMove={onMove}
                                                                        onCancel={setCancelTarget}
                                                                    />
                                                                </div>
                                                            )}
                                                        </Draggable>
                                                    ))}
                                                    {drop.placeholder}
                                                </div>
                                            )}
                                        </Droppable>
                                    </section>
                                );
                            })}
                        </div>
                    </DragDropContext>
                </>
            )}

            <ConfirmDialog
                open={cancelTarget !== null}
                onClose={() => setCancelTarget(null)}
                onConfirm={() => void confirmCancel()}
                busy={cancelling}
                title={t('confirm.title', { number: cancelTarget?.orderNumber ?? '' })}
                confirmLabel={t('confirm.cancel')}
                cancelLabel={t('confirm.keep')}
            >
                {t('confirm.body')}
            </ConfirmDialog>
        </div>
    );
};

/** Admin has to pick a location first; everyone else starts on their own. */
export const OrdersBoard = ({ layout }: { layout: OrdersLayout }) => {
    const { t } = useTranslation('orders');
    const { locationId, isAdmin } = useActiveLocation();
    if (isAdmin && !locationId) {
        return (
            <Card padding="none">
                <StateBlock variant="empty" icon={MapPinIcon} title={t('board.chooseLocation')} body={t('board.chooseLocationBody')} />
            </Card>
        );
    }
    return <LiveBoard key={locationId ?? 'all'} locationId={locationId} layout={layout} />;
};
